import { z } from 'zod';
import { defineListQuery, listFilter } from './list-query.schemas';

/**
 * The collections follow-up log (PRD → Payments and collections; gitaumoses4/chukua#154): what Finance did to chase a customer,
 * optionally about one invoice. The `collection_follow_ups_type_check` constraint hard-codes the same types, so adding one is a migration.
 */
export const FOLLOW_UP_TYPES = ['call', 'email', 'visit', 'whatsapp'] as const;
export type FollowUpType = (typeof FOLLOW_UP_TYPES)[number];

export const FOLLOW_UP_TYPE_LABELS: Record<FollowUpType, string> = {
  call: 'Call',
  email: 'Email',
  visit: 'Visit',
  whatsapp: 'WhatsApp',
};

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((value) => value || undefined);

/**
 * `contactedAt` is when it happened, which may be the day before it is logged; the API refuses a future time. Who did it is the
 * signed-in user, never an input. A correction names the entry it corrects (the old row stays).
 */
export const collectionFollowUpInputSchema = z
  .object({
    type: z.enum(FOLLOW_UP_TYPES),
    contactedAt: z.string().datetime({ offset: true }),
    invoiceId: z.string().uuid().optional(),
    contactName: optionalText(200),
    outcome: z.string().trim().min(1).max(200),
    notes: optionalText(2000),
    correctsFollowUpId: z.string().uuid().optional(),
  })
  .strict();
export type CollectionFollowUpInput = z.infer<typeof collectionFollowUpInputSchema>;

export interface CollectionFollowUpView {
  id: string;
  customerId: string;
  invoiceId: string | null;
  /** Null when the follow-up is about the customer, not one invoice. */
  invoiceNumber: string | null;
  type: FollowUpType;
  contactedAt: string;
  contactName: string | null;
  outcome: string;
  notes: string | null;
  correctsFollowUpId: string | null;
  recordedBy: { id: string; name: string };
  createdAt: string;
}

/** `GET /customers/:id/follow-ups` — search matches outcome, notes and the contact's name. */
export const followUpListQuerySchema = defineListQuery({
  sortFields: ['contactedAt', 'createdAt'],
  defaultSort: { sort: 'contactedAt', order: 'desc' },
  filters: {
    type: listFilter(z.enum(FOLLOW_UP_TYPES)),
    invoiceId: listFilter(z.string().uuid()),
  },
});
export type FollowUpListQuery = z.infer<typeof followUpListQuerySchema>;
